import { useMemo } from 'react'

import type { ChargerModel } from '~/data/api/client/chargers'
import { type CustomerWithPath, isSharedOrg } from '~/data/types/organization'
import { collectChargerEvseIds, evseIdMatchesQuery, formatEvseIdDisplay } from '~/data/utils/evseId'
import routeDirectory from '~/navigation/routes'
import { normalizeStr } from '~/views/overviews/hooks'

import type {
  CategoryTotals,
  FlatItem,
  MatchedOn,
  QuickSearchCommand,
  QuickSearchResults,
  ResultGroup,
  SearchCategory,
  SearchResult
} from '../types'
import { CATEGORY_ORDER, MAX_RESULTS_PER_CATEGORY } from '../types'
import parseSearchFilter from '../utils/parseSearchFilter'
import type { QuickSearchData } from './useQuickSearchData'

export type QuickSearchResultParams = QuickSearchData & {
  query: string
  navigateAndClose: (href: string, replace?: boolean) => void
  cloak: (organization: CustomerWithPath) => void
  /** App-level commands the current user can run; empty when none apply. */
  commands: QuickSearchCommand[]
  expandedCategories: Set<SearchCategory>
  onExpandCategory: (category: SearchCategory) => void
}

export default function useQuickSearchResults({
  query,
  siteItems,
  chargerItems,
  vehicleItems,
  userItems,
  organizations,
  synopLinkItems,
  solarArrayItems,
  pages,
  loadingByCategory,
  navigateAndClose,
  cloak,
  commands,
  expandedCategories,
  onExpandCategory
}: QuickSearchResultParams): QuickSearchResults {
  return useMemo(() => {
    const { category: filterCategory, searchTerm } = parseSearchFilter(query || '')
    const normalizedQuery = normalizeStr(searchTerm)
    if (!normalizedQuery) {
      return {
        groups: [],
        totals: { charger: 0, site: 0, vehicle: 0, user: 0, org: 0, synoplink: 0, solarArray: 0, page: 0 },
        effectiveExpandedCategories: new Set<SearchCategory>(),
        isSingleCategory: false,
        filterCategory,
        searchTerm
      }
    }

    // Helper: only compute items when filter is inactive or matches this category
    const includeIfMatchesFilter = <T>(cat: SearchCategory, buildItems: () => T[]): T[] => {
      if (filterCategory !== null && filterCategory !== cat) return []
      return buildItems()
    }

    // Build all results by category
    const allResults: Record<SearchCategory, SearchResult[]> = {
      site: includeIfMatchesFilter('site', () =>
        siteItems
          .filter((site) => normalizeStr(site?.depotNm)?.includes(normalizedQuery))
          .map((site) => ({
            id: site.depotId,
            category: 'site',
            label: site.depotNm ?? '',
            action: 'view',
            onSelect: () => navigateAndClose(routeDirectory.sites.details(site.depotId))
          }))
      ),

      charger: includeIfMatchesFilter('charger', () =>
        chargerItems.flatMap((charger) => {
          const nameMatches = normalizeStr(charger.chargerName)?.includes(normalizedQuery)
          const idMatches = normalizeStr(charger.chargerId)?.includes(normalizedQuery)
          const matchedEvseId = collectChargerEvseIds(charger).find((evseId) =>
            evseIdMatchesQuery(evseId, searchTerm)
          )

          if (!nameMatches && !idMatches && !matchedEvseId) return []

          const matchedOn = resolveChargerMatchedOn(charger, !!nameMatches, !!idMatches, matchedEvseId)
          return [
            {
              id: charger.chargerId,
              category: 'charger' as const,
              label: charger.chargerName ?? '',
              subLabel: charger.depotName ?? '',
              action: 'view' as const,
              matchedOn,
              onSelect: () => navigateAndClose(routeDirectory.chargers.details(charger.chargerId))
            }
          ]
        })
      ),

      vehicle: includeIfMatchesFilter('vehicle', () =>
        vehicleItems.flatMap((vehicle) => {
          const nameMatches = normalizeStr(vehicle?.vehicleNm)?.includes(normalizedQuery)
          const displayNameMatches = normalizeStr(vehicle?.vehicleDisplayNm)?.includes(normalizedQuery)
          const vinMatches = normalizeStr(vehicle?.vin)?.includes(normalizedQuery)

          if (!nameMatches && !displayNameMatches && !vinMatches) return []

          // Show matchedOn only when match was on VIN (not displayed by default)
          const matchedOnVin = !nameMatches && !displayNameMatches && vinMatches
          return [
            {
              id: vehicle.id ?? '',
              category: 'vehicle' as const,
              label: vehicle.vehicleDisplayNm ?? vehicle.vehicleNm ?? '',
              action: 'view' as const,
              matchedOn: matchedOnVin ? { field: 'vin' as const, value: vehicle.vin } : undefined,
              onSelect: () => navigateAndClose(routeDirectory.vehicles.details(vehicle.id ?? ''))
            }
          ]
        })
      ),

      user: includeIfMatchesFilter('user', () =>
        (userItems ?? [])
          .filter((user) => normalizeStr(user?.name)?.includes(normalizedQuery) && user.id)
          .map((user) => ({
            id: user.id!,
            category: 'user' as const,
            label: user.name ?? '',
            action: 'view' as const,
            onSelect: () => navigateAndClose(routeDirectory.settings.users.edit(user.id!))
          }))
      ),

      org: includeIfMatchesFilter('org', () =>
        organizations
          // Shared orgs aren't cloakable (the only action on org results) — the
          // receiving org has view access to their assets but no manage
          // authority. Drop them from results so users don't select an org
          // that does nothing on click.
          .filter((org) => !isSharedOrg(org))
          .filter((org) => normalizeStr(org?.organizationNm)?.includes(normalizedQuery) && org.id)
          .map((org) => {
            const { full, truncated } = buildOrgBreadcrumbs(org.path)
            return {
              id: org.id!,
              category: 'org',
              label: org.organizationNm ?? '',
              subLabel: truncated,
              fullLabel: full,
              action: 'cloak',
              onSelect: () => {
                cloak(org)
                navigateAndClose(routeDirectory.vehicles.root, true)
              }
            }
          })
      ),

      synoplink: includeIfMatchesFilter('synoplink', () =>
        synopLinkItems.flatMap((sl) => {
          const nickNameMatches = normalizeStr(sl.nickName)?.includes(normalizedQuery)
          const thingNameMatches = normalizeStr(sl.thingName)?.includes(normalizedQuery)
          const serialNumberMatches = normalizeStr(sl.serialNumber ?? undefined)?.includes(normalizedQuery)

          if (!nickNameMatches && !thingNameMatches && !serialNumberMatches) return []

          const matchedOnThingName = !nickNameMatches && thingNameMatches
          const matchedOnSerialNumber = !nickNameMatches && !thingNameMatches && serialNumberMatches
          const matchedOn = matchedOnThingName
            ? { field: 'synoplinkId' as const, value: sl.thingName }
            : matchedOnSerialNumber
              ? { field: 'serialNumber' as const, value: sl.serialNumber ?? '' }
              : undefined
          return [
            {
              id: sl.id,
              category: 'synoplink' as const,
              label: sl.nickName || sl.thingName,
              action: 'view' as const,
              matchedOn,
              onSelect: () => navigateAndClose(routeDirectory.links.details(sl.id))
            }
          ]
        })
      ),

      solarArray: includeIfMatchesFilter('solarArray', () =>
        solarArrayItems
          .filter(
            // Match the id too (not just name) so an id pasted from the URL or a
            // sa: filter finds a named array, matching the other categories.
            (sa) => normalizeStr(sa.name)?.includes(normalizedQuery) || normalizeStr(sa.id).includes(normalizedQuery)
          )
          .map((sa) => ({
            id: sa.id,
            category: 'solarArray' as const,
            label: sa.name || sa.id,
            action: 'view' as const,
            onSelect: () => navigateAndClose(routeDirectory.solarArrays.details(sa.id))
          }))
      ),

      page: includeIfMatchesFilter('page', () => {
        const pageResults: SearchResult[] = pages
          .filter(
            (page) =>
              normalizeStr(page.label).includes(normalizedQuery) ||
              (page.subLabel && normalizeStr(page.subLabel).includes(normalizedQuery)) ||
              page.keywords?.some((kw) => normalizeStr(kw).includes(normalizedQuery))
          )
          .map((page) => ({
            id: page.id,
            category: 'page',
            label: page.label,
            subLabel: page.subLabel,
            action: 'navigate',
            icon: page.icon,
            onSelect: () => navigateAndClose(page.href)
          }))

        // Commands are not pages — surface them under Pages when their label
        // matches. Availability is decided by the caller, which omits any the
        // user can't run.
        const matchingCommands = commands.filter((command) =>
          normalizeStr(command.label).includes(normalizedQuery)
        )
        pageResults.unshift(
          ...matchingCommands.map<SearchResult>((command) => ({
            id: command.id,
            category: 'page',
            label: command.label,
            action: 'action',
            icon: command.icon,
            onSelect: command.onSelect
          }))
        )

        return pageResults
      })
    }

    // Store totals before slicing
    const totals: CategoryTotals = {
      charger: allResults.charger.length,
      site: allResults.site.length,
      vehicle: allResults.vehicle.length,
      user: allResults.user.length,
      org: allResults.org.length,
      synoplink: allResults.synoplink.length,
      solarArray: allResults.solarArray.length,
      page: allResults.page.length
    }

    // Determine which categories have results
    const categoriesWithResults = CATEGORY_ORDER.filter((cat) => totals[cat] > 0)
    const loadingCategories = CATEGORY_ORDER.filter((category) => {
      if (filterCategory !== null && filterCategory !== category) return false

      return loadingByCategory[category] && totals[category] === 0
    })
    const visibleCategories = CATEGORY_ORDER.filter(
      (category) => totals[category] > 0 || loadingCategories.includes(category)
    )
    const isSingleCategory = categoriesWithResults.length === 1

    // Build effective expanded categories set (includes auto-expanded single category)
    const effectiveExpandedCategories = new Set(expandedCategories)
    if (isSingleCategory && categoriesWithResults[0]) {
      effectiveExpandedCategories.add(categoriesWithResults[0])
    }

    const groups: ResultGroup[] = visibleCategories.map((category) => {
      if (loadingCategories.includes(category)) {
        return {
          category,
          items: [],
          isLoading: true
        }
      }

      const isExpanded = effectiveExpandedCategories.has(category)
      const results = isExpanded ? allResults[category] : allResults[category].slice(0, MAX_RESULTS_PER_CATEGORY)
      const hasHiddenResults = !isExpanded && totals[category] > MAX_RESULTS_PER_CATEGORY

      const items: FlatItem[] = hasHiddenResults
        ? [
            ...results,
            {
              type: 'viewAll',
              category,
              hiddenCount: totals[category] - MAX_RESULTS_PER_CATEGORY,
              onSelect: () => onExpandCategory(category)
            }
          ]
        : results

      return { category, items, isLoading: false }
    })

    return {
      groups,
      totals,
      effectiveExpandedCategories,
      isSingleCategory,
      filterCategory,
      searchTerm
    }
  }, [
    chargerItems,
    cloak,
    commands,
    expandedCategories,
    loadingByCategory,
    navigateAndClose,
    onExpandCategory,
    organizations,
    synopLinkItems,
    solarArrayItems,
    pages,
    query,
    siteItems,
    userItems,
    vehicleItems
  ])
}

/**
 * Which non-displayed field a charger matched on, or undefined when the query hit the
 * charger's own name and the row already shows why it is here.
 *
 * Charger ID outranks EVSE ID so a charger whose connectors carry EVSE IDs still reports the
 * same field it did before EVSE IDs were searchable.
 */
function resolveChargerMatchedOn(
  charger: ChargerModel,
  nameMatches: boolean,
  idMatches: boolean,
  matchedEvseId: string | undefined
): MatchedOn | undefined {
  if (nameMatches) return undefined
  if (idMatches) return { field: 'chargerId', value: charger.chargerId }
  if (matchedEvseId) return { field: 'evseId', value: formatEvseIdDisplay(matchedEvseId) }
  return undefined
}

/**
 * Builds breadcrumb strings for an organization's path.
 * - Removes first element (user's current org) and last element (the org itself)
 * - Returns undefined if path has 2 or fewer elements (no breadcrumb trail to show)
 */
function buildOrgBreadcrumbs(path: CustomerWithPath['path']): { full?: string; truncated?: string } {
  if (path.length <= 2) return {}

  const pathWithoutEnds = path.slice(1, -1)

  // Full breadcrumb for tooltip
  const full = pathWithoutEnds.map((p) => p.name).join(' / ')

  // Truncated breadcrumb: first parent full name, subsequent parents 3 chars + "..."
  const truncated = pathWithoutEnds.map((p, i) => (i === 0 ? p.name : p.name.slice(0, 3) + '...')).join(' / ')

  return { full, truncated }
}
